<template>
  <div class="calendario-general">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div>
        <h4 class="mb-25">Calendario General</h4>
        <small class="text-muted">Vista de todas las vacaciones y festivos</small>
      </div>
      <div class="d-flex align-items-center">
        <b-button
          v-if="diasSeleccionados.length > 0"
          variant="primary"
          size="sm"
          class="mr-1"
          @click="abrirModalAsignar"
        >
          Asignar Vacaciones ({{ diasSeleccionados.length }})
        </b-button>
        <b-button
          v-if="diasSeleccionados.length > 0"
          variant="outline-secondary"
          size="sm"
          class="mr-1"
          @click="limpiarSeleccion"
        >
          Limpiar
        </b-button>
        <feather-icon icon="CalendarIcon" size="18" class="text-primary mr-75" />
        <span class="font-weight-bold mr-1">Año:</span>
        <b-form-select v-model="anioSeleccionado" :options="aniosDisponibles" size="sm" style="width: 100px" @change="cargarDatos" />
      </div>
    </div>

    <full-calendar ref="refCalendar" :options="calendarOptions" class="full-calendar calendario-admin" />

    <!-- Leyenda -->
    <div class="d-flex flex-wrap mt-2 pt-1 border-top">
      <div class="leyenda-item">
        <span class="leyenda-color" style="background-color: #a855f7"></span>
        <small>Festivo Nacional</small>
      </div>
      <div class="leyenda-item">
        <span class="leyenda-color" style="background-color: #6366f1"></span>
        <small>Aprobadas</small>
      </div>
      <div class="leyenda-item">
        <span class="leyenda-color" style="background-color: #ff9f43"></span>
        <small>Vacaciones Pendientes</small>
      </div>
      <div v-if="diasSeleccionados.length > 0" class="leyenda-item">
        <span class="leyenda-color" style="background-color: #7367f0"></span>
        <small>Día Seleccionado</small>
      </div>
    </div>

    <!-- Resumen de usuarios -->
    <div v-if="resumenUsuarios.length > 0" class="mt-2">
      <h5 class="mb-1">Resumen por usuario</h5>
      <b-table-simple small bordered responsive hover class="mb-0">
        <b-thead head-variant="light">
          <b-tr>
            <b-th>Usuario</b-th>
            <b-th class="text-center">Total</b-th>
            <b-th class="text-center">Usados</b-th>
            <b-th class="text-center">Pendientes</b-th>
            <b-th class="text-center">Disponibles</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="u in resumenUsuarios" :key="u.usuario_id">
            <b-td>{{ u.usuario_nombre }}</b-td>
            <b-td class="text-center">{{ u.dias_totales }}</b-td>
            <b-td class="text-center">
              <b-badge variant="success">{{ u.dias_usados }}</b-badge>
            </b-td>
            <b-td class="text-center">
              <b-badge v-if="u.dias_pendientes > 0" variant="warning">{{ u.dias_pendientes }}</b-badge>
              <span v-else>0</span>
            </b-td>
            <b-td class="text-center">
              <b-badge :variant="(u.dias_totales - u.dias_usados - u.dias_pendientes) > 0 ? 'primary' : 'secondary'">
                {{ u.dias_totales - u.dias_usados - u.dias_pendientes }}
              </b-badge>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>

    <!-- Modal detalle -->
    <b-modal
      v-model="mostrarModal"
      :title="modalTitulo"
      centered
      size="sm"
    >
      <div v-if="eventoSeleccionado">
        <p><strong>Fecha:</strong> {{ formatearFecha(eventoSeleccionado.fecha) }}</p>

        <!-- Click en usuario de festivo -->
        <template v-if="eventoSeleccionado.tipo === 'festivo'">
          <p><strong>Festivo:</strong> {{ eventoSeleccionado.nombre }}</p>
          <p><strong>Usuario:</strong> {{ eventoSeleccionado.usuario }}</p>
          <p><b-badge variant="warning">Festivo Nacional</b-badge></p>
        </template>

        <!-- Click en vacaciones -->
        <template v-else>
          <p><strong>Usuario:</strong> {{ eventoSeleccionado.usuario }}</p>
          <p><strong>Tipo:</strong> {{ eventoSeleccionado.tipoVacacion }}</p>
          <p>
            <strong>Estado:</strong>
            <b-badge :variant="eventoSeleccionado.estado === 'aprobada' ? 'success' : 'warning'">
              {{ eventoSeleccionado.estado }}
            </b-badge>
          </p>
        </template>
      </div>

      <template #modal-footer>
        <b-button
          v-if="eventoSeleccionado && eventoSeleccionado.tipo === 'solicitud'"
          variant="danger"
          size="sm"
          :disabled="eliminando"
          @click="eliminarSolicitud"
        >
          <b-spinner v-if="eliminando" small class="mr-50" />
          Eliminar
        </b-button>
        <b-button variant="secondary" size="sm" @click="mostrarModal = false">
          Cerrar
        </b-button>
      </template>
    </b-modal>

    <!-- Modal asignar vacaciones -->
    <b-modal
      v-model="mostrarModalAsignar"
      title="Asignar Vacaciones"
      centered
      size="md"
      no-close-on-backdrop
      @hidden="cancelarAsignar"
    >
      <!-- Fechas seleccionadas -->
      <div class="mb-1">
        <label class="font-weight-bold d-block mb-50">Fechas seleccionadas:</label>
        <div class="d-flex flex-wrap" style="gap: 6px">
          <b-badge
            v-for="fecha in diasSeleccionadosOrdenados"
            :key="fecha"
            variant="primary"
            class="d-flex align-items-center px-75 py-50"
            style="font-size: 0.8rem"
          >
            {{ formatearFechaCorta(fecha) }}
            <feather-icon
              icon="XIcon"
              size="14"
              class="ml-50 cursor-pointer"
              @click="quitarDia(fecha)"
            />
          </b-badge>
        </div>
      </div>

      <!-- Selector de usuarios -->
      <div class="mb-1">
        <label class="font-weight-bold d-block mb-50">Usuarios:</label>
        <div v-if="cargandoUsuarios" class="text-center py-1">
          <b-spinner small variant="primary" />
          <span class="ml-50">Cargando usuarios...</span>
        </div>
        <div v-else-if="usuariosDisponibles.length === 0" class="text-muted">
          No hay usuarios con calendario asignado para {{ anioSeleccionado }}
        </div>
        <div v-else>
          <div class="mb-50">
            <b-button variant="outline-primary" size="sm" class="mr-50" @click="seleccionarTodosUsuarios">
              Todos
            </b-button>
            <b-button variant="outline-warning" size="sm" class="mr-50" @click="seleccionarTallerUsuarios">
              Taller
            </b-button>
            <b-button variant="outline-secondary" size="sm" @click="usuariosSeleccionados = []">
              Ninguno
            </b-button>
          </div>
          <div class="usuarios-lista border rounded p-75" style="max-height: 200px; overflow-y: auto">
            <b-form-checkbox
              v-for="u in usuariosDisponibles"
              :key="u.usuario_id"
              v-model="usuariosSeleccionados"
              :value="u.usuario_id"
              class="mb-25"
            >
              {{ u.usuario_nombre }}
            </b-form-checkbox>
          </div>
        </div>
      </div>

      <!-- Tipo -->
      <div class="mb-1">
        <label class="font-weight-bold d-block mb-50">Tipo:</label>
        <b-form-radio-group
          v-model="tipoAsignar"
          :options="[
            { text: 'Día Completo', value: 'completo' },
            { text: 'Mañana', value: 'manana' },
            { text: 'Tarde', value: 'tarde' },
          ]"
        />
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="mostrarModalAsignar = false">
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="usuariosSeleccionados.length === 0 || diasSeleccionados.length === 0 || asignando"
          @click="asignarVacaciones"
        >
          <b-spinner v-if="asignando" small class="mr-50" />
          Asignar ({{ usuariosSeleccionados.length }} usuario{{ usuariosSeleccionados.length !== 1 ? 's' : '' }} x {{ diasSeleccionados.length }} día{{ diasSeleccionados.length !== 1 ? 's' : '' }})
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { BFormSelect, BModal, BBadge, BButton, BFormCheckbox, BFormRadioGroup, BSpinner, BTableSimple, BThead, BTbody, BTr, BTh, BTd } from 'bootstrap-vue'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import esLocale from '@fullcalendar/core/locales/es'
import axios from '@/libs/axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BFormSelect,
    BModal,
    BBadge,
    BButton,
    BFormCheckbox,
    BFormRadioGroup,
    BSpinner,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    FullCalendar,
  },

  data() {
    return {
      anioSeleccionado: new Date().getFullYear(),
      solicitudes: [],
      festivos: [],
      mostrarModal: false,
      eventoSeleccionado: null,
      // Asignar vacaciones
      diasSeleccionados: [],
      mostrarModalAsignar: false,
      usuariosDisponibles: [],
      usuariosSeleccionados: [],
      tipoAsignar: 'completo',
      asignando: false,
      cargandoUsuarios: false,
      eliminando: false,
      resumenUsuarios: [],
      usuariosTaller: [22,34,35,36,37,38,39,40,41]
    }
  },

  computed: {
    aniosDisponibles() {
      const actual = new Date().getFullYear()
      return [actual - 1, actual, actual + 1].map(a => ({ value: a, text: a.toString() }))
    },

    diasSeleccionadosOrdenados() {
      return this.diasSeleccionados.slice().sort()
    },

    modalTitulo() {
      if (!this.eventoSeleccionado) return ''
      if (this.eventoSeleccionado.tipo === 'festivo-titulo') {
        return this.eventoSeleccionado.nombre
      }
      if (this.eventoSeleccionado.tipo === 'festivo') {
        return `Festivo - ${this.eventoSeleccionado.usuario}`
      }
      return `Vacaciones - ${this.eventoSeleccionado.usuario}`
    },

    eventos() {
      const eventos = []

      // Días seleccionados (fondo azul)
      this.diasSeleccionados.forEach(fecha => {
        eventos.push({
          id: `selected-${fecha}`,
          start: fecha,
          allDay: true,
          display: 'background',
          backgroundColor: 'rgba(115, 103, 240, 0.25)',
        })
      })

      // Festivos (fondo morado en toda la celda + eventos por usuario, agrupando taller)
      var usuariosTaller = this.usuariosTaller
      this.festivos.forEach(f => {
        const fechaStr = typeof f.fecha === 'string' ? f.fecha.split('T')[0] : f.fecha

        // Fondo morado para el día
        eventos.push({
          id: `festivo-bg-${f.id}`,
          start: fechaStr,
          allDay: true,
          display: 'background',
          backgroundColor: 'rgba(168, 85, 247, 0.2)',
        })

        if (f.usuarios && f.usuarios.length > 0) {
          var idsFestivo = f.usuarios.map(function(u) { return u.id })
          var todoTaller = usuariosTaller.every(function(id) { return idsFestivo.indexOf(id) !== -1 })

          if (todoTaller) {
            eventos.push({
              id: `festivo-${f.id}-taller`,
              title: 'TALLER',
              start: fechaStr,
              allDay: true,
              backgroundColor: '#a855f7',
              borderColor: '#a855f7',
              extendedProps: { tipo: 'festivo', nombre: f.nombre, usuario: 'TALLER', fecha: fechaStr },
            })
            f.usuarios.forEach(function(u) {
              if (usuariosTaller.indexOf(u.id) === -1) {
                eventos.push({
                  id: `festivo-${f.id}-user-${u.id}`,
                  title: u.nombre,
                  start: fechaStr,
                  allDay: true,
                  backgroundColor: '#a855f7',
                  borderColor: '#a855f7',
                  classNames: ['festivo-usuario'],
                  extendedProps: { tipo: 'festivo', nombre: f.nombre, usuario: u.nombre, fecha: fechaStr },
                })
              }
            })
          } else {
            f.usuarios.forEach(function(u) {
              eventos.push({
                id: `festivo-${f.id}-user-${u.id}`,
                title: u.nombre,
                start: fechaStr,
                allDay: true,
                backgroundColor: '#a855f7',
                borderColor: '#a855f7',
                classNames: ['festivo-usuario'],
                extendedProps: { tipo: 'festivo', nombre: f.nombre, usuario: u.nombre, fecha: fechaStr },
              })
            })
          }
        }
      })

      // Solicitudes de vacaciones (agrupar taller si todos tienen día completo aprobado)
      var solPorFecha = {}
      this.solicitudes.forEach(function(s) {
        var fechaStr = typeof s.fecha === 'string' ? s.fecha.split('T')[0] : s.fecha
        if (!solPorFecha[fechaStr]) solPorFecha[fechaStr] = []
        solPorFecha[fechaStr].push(s)
      })

      Object.keys(solPorFecha).forEach(function(fecha) {
        var items = solPorFecha[fecha]
        var tallerAprobados = items.filter(function(s) {
          return usuariosTaller.indexOf(s.usuario_id) !== -1 && s.estado === 'aprobada' && s.tipo === 'completo'
        })
        var todoTaller = usuariosTaller.every(function(id) {
          return tallerAprobados.some(function(s) { return s.usuario_id === id })
        })

        if (todoTaller) {
          eventos.push({
            id: 'solicitud-taller-' + fecha,
            title: 'TALLER',
            start: fecha,
            allDay: true,
            backgroundColor: '#6366f1',
            borderColor: '#6366f1',
            extendedProps: { tipo: 'solicitud', usuario: 'TALLER', tipoVacacion: 'Día completo', estado: 'aprobada', fecha: fecha },
          })
          items.forEach(function(s) {
            if (usuariosTaller.indexOf(s.usuario_id) !== -1 && s.estado === 'aprobada' && s.tipo === 'completo') return
            var esAprobada = s.estado === 'aprobada'
            var esMedioDia = s.tipo === 'manana' || s.tipo === 'tarde'
            var color = esAprobada ? '#6366f1' : '#ff9f43'
            var titulo = s.usuario_nombre
            if (s.tipo === 'manana') titulo += ' (M)'
            if (s.tipo === 'tarde') titulo += ' (T)'
            eventos.push({
              id: 'solicitud-' + s.id,
              title: titulo,
              start: fecha,
              allDay: true,
              backgroundColor: color,
              borderColor: color,
              extendedProps: { tipo: 'solicitud', solicitud_id: s.id, usuario: s.usuario_nombre_completo || s.usuario_nombre, tipoVacacion: s.tipo === 'completo' ? 'Día completo' : s.tipo === 'manana' ? 'Mañana' : 'Tarde', estado: s.estado, fecha: fecha },
            })
          })
        } else {
          items.forEach(function(s) {
            var esAprobada = s.estado === 'aprobada'
            var esMedioDia = s.tipo === 'manana' || s.tipo === 'tarde'
            var color = esAprobada ? '#6366f1' : '#ff9f43'
            var titulo = s.usuario_nombre
            if (s.tipo === 'manana') titulo += ' (M)'
            if (s.tipo === 'tarde') titulo += ' (T)'
            eventos.push({
              id: 'solicitud-' + s.id,
              title: titulo,
              start: fecha,
              allDay: true,
              backgroundColor: color,
              borderColor: color,
              extendedProps: { tipo: 'solicitud', solicitud_id: s.id, usuario: s.usuario_nombre_completo || s.usuario_nombre, tipoVacacion: s.tipo === 'completo' ? 'Día completo' : s.tipo === 'manana' ? 'Mañana' : 'Tarde', estado: s.estado, fecha: fecha },
            })
          })
        }
      })

      return eventos
    },

    calendarOptions() {
      return {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locale: esLocale,
        headerToolbar: {
          start: 'prev,next today',
          center: 'title',
          end: '',
        },
        height: 'auto',
        events: this.eventos,
        selectable: false,
        dateClick: this.handleDateClick,
        eventClick: this.handleEventClick,
        dayMaxEvents: false,
        eventDidMount: this.handleEventMount,
        dayCellDidMount: this.handleDayCellMount,
        eventOrder: function(a, b) {
          var aTaller = a.extendedProps && a.extendedProps.usuario === 'TALLER' ? 0 : 1
          var bTaller = b.extendedProps && b.extendedProps.usuario === 'TALLER' ? 0 : 1
          return aTaller - bTaller
        },
      }
    },
  },

  mounted() {
    this.cargarDatos()
  },

  methods: {
    async cargarDatos() {
      try {
        const [calRes, resumenRes] = await Promise.all([
          axios.get('/vacaciones/admin/calendario', {
            params: { anio: this.anioSeleccionado },
            headers: { 'x-token': localStorage.getItem('token') },
          }),
          axios.get('/vacaciones/admin/resumen-usuarios', {
            params: { anio: this.anioSeleccionado },
            headers: { 'x-token': localStorage.getItem('token') },
          }),
        ])
        this.solicitudes = calRes.data.solicitudes || []
        this.festivos = calRes.data.festivos || []
        this.resumenUsuarios = resumenRes.data || []
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error al cargar calendario',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    handleDateClick(info) {
      const fecha = info.dateStr
      const idx = this.diasSeleccionados.indexOf(fecha)
      if (idx >= 0) {
        this.diasSeleccionados.splice(idx, 1)
      } else {
        this.diasSeleccionados.push(fecha)
      }
    },

    handleEventClick(info) {
      const props = info.event.extendedProps
      if (!props || !props.tipo) return // Ignorar eventos de fondo

      if (props.tipo === 'festivo') {
        this.eventoSeleccionado = {
          tipo: 'festivo',
          nombre: props.nombre,
          usuario: props.usuario,
          fecha: props.fecha,
        }
      } else {
        this.eventoSeleccionado = {
          tipo: 'solicitud',
          solicitud_id: props.solicitud_id,
          usuario: props.usuario,
          tipoVacacion: props.tipoVacacion,
          estado: props.estado,
          fecha: props.fecha,
        }
      }
      this.mostrarModal = true
    },

    limpiarSeleccion() {
      this.diasSeleccionados = []
    },

    quitarDia(fecha) {
      const idx = this.diasSeleccionados.indexOf(fecha)
      if (idx >= 0) this.diasSeleccionados.splice(idx, 1)
      if (this.diasSeleccionados.length === 0) this.mostrarModalAsignar = false
    },

    async abrirModalAsignar() {
      this.mostrarModalAsignar = true
      this.usuariosSeleccionados = []
      this.tipoAsignar = 'completo'
      await this.cargarUsuarios()
    },

    async cargarUsuarios() {
      this.cargandoUsuarios = true
      try {
        const { data } = await axios.get('/vacaciones/asignaciones', {
          params: { anio: this.anioSeleccionado },
          headers: { 'x-token': localStorage.getItem('token') },
        })
        this.usuariosDisponibles = data || []
      } catch (error) {
        this.usuariosDisponibles = []
      }
      this.cargandoUsuarios = false
    },

    seleccionarTodosUsuarios() {
      this.usuariosSeleccionados = this.usuariosDisponibles.map(u => u.usuario_id)
    },

    seleccionarTallerUsuarios() {
      const idsTaller = this.usuariosTaller;
      this.usuariosSeleccionados = this.usuariosDisponibles
        .filter(u => idsTaller.includes(u.usuario_id))
        .map(u => u.usuario_id)
    },

    cancelarAsignar() {
      this.asignando = false
    },

    async asignarVacaciones() {
      this.asignando = true
      try {
        const { data } = await axios.post('/vacaciones/admin/solicitudes/crear', {
          usuario_ids: this.usuariosSeleccionados,
          fechas: this.diasSeleccionados,
          tipo: this.tipoAsignar,
        }, {
          headers: { 'x-token': localStorage.getItem('token') },
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: data.message || 'Vacaciones asignadas',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        this.mostrarModalAsignar = false
        this.diasSeleccionados = []
        this.usuariosSeleccionados = []
        await this.cargarDatos()
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Error al asignar vacaciones'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: msg,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
      this.asignando = false
    },

    async eliminarSolicitud() {
      if (!this.eventoSeleccionado || !this.eventoSeleccionado.solicitud_id) return
      this.eliminando = true
      try {
        await axios.delete('/vacaciones/admin/solicitudes/' + this.eventoSeleccionado.solicitud_id, {
          headers: { 'x-token': localStorage.getItem('token') },
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Solicitud eliminada',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.mostrarModal = false
        await this.cargarDatos()
      } catch (error) {
        var msg = (error.response && error.response.data && error.response.data.error) || 'Error al eliminar'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: msg,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
      this.eliminando = false
    },

    formatearFecha(fecha) {
      return new Date(fecha).toLocaleDateString('es-ES', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    },

    formatearFechaCorta(fecha) {
      var parts = fecha.split('-')
      return parts[2] + '/' + parts[1]
    },

    handleEventMount(info) {
      const harness = info.el.closest('.fc-daygrid-event-harness')
      if (!harness) return
      var props = info.event.extendedProps
      if (!props) return

      var esTaller = props.usuario === 'TALLER'
      if (esTaller) {
        harness.style.width = '99%'
        harness.style.maxWidth = '99%'
      } else if (props.tipo === 'festivo') {
        harness.style.width = '49%'
        harness.style.maxWidth = '49%'
      }
    },

    handleDayCellMount(info) {
      // Formatear fecha para comparar
      const d = info.date
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

      // Buscar si hay festivo en este día
      const festivo = this.festivos.find(f => {
        const fechaFestivo = typeof f.fecha === 'string' ? f.fecha.split('T')[0] : f.fecha
        return fechaFestivo === dateStr
      })

      if (festivo) {
        // Añadir el nombre del festivo junto al número del día
        const dayNumber = info.el.querySelector('.fc-daygrid-day-top')
        if (dayNumber) {
          const festivoLabel = document.createElement('span')
          festivoLabel.className = 'festivo-label'
          festivoLabel.textContent = festivo.nombre
          festivoLabel.style.cssText = 'color: #a855f7; font-size: 0.65rem; font-weight: 600; margin-left: 5px; font-style: italic;'
          dayNumber.appendChild(festivoLabel)
        }
      }
    },
  },
}
</script>

<style lang="scss">
.calendario-general {
  .leyenda-item {
    display: flex;
    align-items: center;
    margin-right: 1.25rem;
    margin-bottom: 0.5rem;

    .leyenda-color {
      width: 12px;
      height: 12px;
      border-radius: 3px;
      margin-right: 0.5rem;
    }

    small {
      color: #6e6b7b;
    }
  }

  .calendario-admin {
    .fc-toolbar {
      .fc-toolbar-title {
        font-size: 1.25rem;
        font-weight: 600;
        text-transform: capitalize;
      }

      .fc-button {
        padding: 0.4rem 0.8rem;
        font-size: 0.9rem;
        border-radius: 0.358rem;

        &.fc-button-primary {
          background-color: #7367f0;
          border-color: #7367f0;

          &:hover {
            background-color: #5e50ee;
            border-color: #5e50ee;
          }
        }
      }
    }

    .fc-col-header-cell {
      padding: 0.75rem 0;
      background-color: #f8f8f8;

      .fc-col-header-cell-cushion {
        font-weight: 600;
        color: #5e5873;
        text-transform: uppercase;
        font-size: 0.75rem;
      }
    }

    .fc-daygrid-day {
      border-color: #ebe9f1;
      cursor: pointer;

      &:hover {
        background-color: rgba(115, 103, 240, 0.06);
      }

      // Sábados y domingos (gris claro)
      &.fc-day-sat,
      &.fc-day-sun {
        background-color: #f0f0f0;
      }
    }

    .fc-daygrid-day-frame {
      min-height: 100px;
    }

    .fc-daygrid-day-events {
      display: flex !important;
      flex-wrap: wrap !important;
      align-content: flex-start !important;
      justify-content: flex-start !important;
      gap: 2px !important;
      padding: 2px !important;
      position: relative !important;
      margin: 0 !important;
    }

    // Ocultar harnesses vacíos/espaciadores que genera FullCalendar
    .fc-daygrid-day-events > .fc-daygrid-event-harness:empty {
      display: none !important;
    }

    // Contenedores de eventos al 49%
    .fc-daygrid-day-events > .fc-daygrid-event-harness {
      position: relative !important;
      margin: 0 !important;
      padding: 0 !important;
      width: 48%;
      max-width: 48%;
      min-width: 0 !important;
      inset: unset !important;
      top: unset !important;
      left: unset !important;
      right: unset !important;
      flex-shrink: 0 !important;

      &:first-child {
        margin-left: -2px !important;
      }
    }

    // Fondo de eventos (background events) no debe ocupar espacio en el flex
    .fc-daygrid-day-events > .fc-daygrid-bg-harness {
      position: absolute !important;
      width: 100% !important;
      max-width: 100% !important;
      inset: 0 !important;
      pointer-events: none !important;
    }

    .fc-daygrid-event {
      position: relative !important;
      border-radius: 0.2rem;
      padding: 2px 4px !important;
      font-size: 0.75rem;
      margin: 0 !important;
      inset: unset !important;
      width: 100% !important;
      left: 0 !important;
      right: 0 !important;
      box-sizing: border-box !important;

      .fc-event-main {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .fc-event-title {
        font-weight: 500;
      }

      &.festivo-titulo {
        font-weight: 600;
        font-style: italic;
      }
    }

    // El título del festivo ocupa todo el ancho
    .fc-daygrid-day-events > .festivo-titulo-harness,
    .fc-daygrid-event-harness.festivo-titulo-harness {
      width: 100% !important;
      max-width: 100% !important;
      flex-basis: 100% !important;
    }

    // Los usuarios de festivo ocupan 50%
    .fc-daygrid-day-events > .festivo-usuario-harness,
    .fc-daygrid-event-harness.festivo-usuario-harness {
      width: 50% !important;
      max-width: 50% !important;
    }

    .fc-daygrid-day-bottom {
      width: 100% !important;
      max-width: 100% !important;
    }

    .fc-daygrid-more-link {
      font-size: 0.7rem;
      color: #7367f0;
      font-weight: 500;
    }
  }
}

.dark-layout {
  .calendario-general {
    .calendario-admin {
      .fc-col-header-cell {
        background-color: #283046;

        .fc-col-header-cell-cushion {
          color: #b4b7bd;
        }
      }

      .fc-daygrid-day {
        border-color: #3b4253;

        &.fc-day-sat,
        &.fc-day-sun {
          background-color: #1e2536;
        }
      }
    }

    .leyenda-item small {
      color: #b4b7bd;
    }
  }
}

.fc-next-button{
  margin-left: 10px !important;
}
</style>
